import config from '../config';

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
  }

  // Get the current user's auth token
  getAuthToken() {
    const savedUser = localStorage.getItem('authUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        return user.credential; // This is the Google JWT token
      } catch (error) {
        console.error('Error parsing saved user:', error);
        return null;
      }
    }
    return null;
  }

  // Create authenticated headers
  getAuthHeaders() {
    const token = this.getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Generic authenticated fetch method
  async authenticatedFetch(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      headers: this.getAuthHeaders(),
      ...options,
    };

    // Merge headers if options.headers exists
    if (options.headers) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        ...options.headers,
      };
    }

    try {
      const response = await fetch(url, defaultOptions);
      
      // Handle authentication errors
      if (response.status === 401) {
        // Token expired or invalid - LOG IT FIRST
        console.error('🚨 401 Unauthorized response from:', url);
        console.error('Request headers:', defaultOptions.headers);
        
        // Temporarily comment out auto-logout to debug
        // localStorage.removeItem('authUser');
        // window.location.reload();
        throw new Error('Authentication expired. Please sign in again.');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Specific API methods
  async searchByMandate(mandateId, limit = 200) {
    const response = await this.authenticatedFetch('/search_by_mandate', {
      method: 'POST',
      body: JSON.stringify({ mandate_id: mandateId, limit })
    });
    return response.json();
  }

  async nlpSearch(searchData) {
    const response = await this.authenticatedFetch('/search', {
      method: 'POST',
      body: JSON.stringify(searchData)
    });
    return response.json();
  }

  async getRecentMandates() {
    const response = await this.authenticatedFetch('/recent_mandates');
    return response.json();
  }

  async getAllMandates() {
    const response = await this.authenticatedFetch('/all_mandates');
    return response.json();
  }

  async getCandidateLinkedMandates(candidateId) {
    const response = await this.authenticatedFetch(`/candidate_linked_mandates?candidate_id=${candidateId}`);
    return response.json();
  }

  async getCandidatesLinkedMandates(candidateIds) {
    const response = await this.authenticatedFetch('/candidates_linked_mandates', {
      method: 'POST',
      body: JSON.stringify(candidateIds)
    });
    return response.json();
  }

  async getMandateLinkedCandidates(mandateId) {
    const response = await this.authenticatedFetch(`/mandate_linked_candidates?mandate_id=${mandateId}`);
    return response.json();
  }

  async getLinkedCandidatesMatchScores(mandateId, candidateIds) {
    const response = await this.authenticatedFetch('/linked_candidates_match_scores', {
      method: 'POST',
      body: JSON.stringify({
        mandate_id: mandateId,
        candidate_ids: candidateIds
      })
    });
    return response.json();
  }

  async getCandidateDetails(candidateId) {
    const response = await this.authenticatedFetch(`/candidate_details?candidate_id=${candidateId}`);
    return response.json();
  }

  // Similar candidates and mandates
  async getSimilarCandidates(candidateId, limit = 50) {
    const response = await this.authenticatedFetch('/similar_candidates', {
      method: 'POST',
      body: JSON.stringify({ candidate_id: candidateId, limit })
    });
    return response.json();
  }

  async getSimilarMandates(mandateId, limit = 50) {
    const response = await this.authenticatedFetch('/similar_mandates', {
      method: 'POST',
      body: JSON.stringify({ mandate_id: mandateId, limit })
    });
    return response.json();
  }

  async getMandateDetails(mandateId) {
    const response = await this.authenticatedFetch(`/mandate_details?mandate_id=${mandateId}`);
    return response.json();
  }
}

// Export a singleton instance
const apiService = new ApiService();
export default apiService; 